import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { FormInputFile } from '@/components/forms/form-input-file.js';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { createLessonsByCsv, getLessons } from '@/lib/api-calls.js';
import { csvFormSchema, parseCsvAndUpload } from '@/lib/csv-utils.js';
import { pluralize } from '@/lib/string-utils.js';
import { CreateLessonCsvFormKeys, createLessonCsvRow } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { z } from 'zod';

interface CreateLessonsByCsvProps {
  trigger: ReactNode;
}

const EMPTY_ERRORS: string[] = [];
const EMPTY_WARNINGS: string[] = [];
const formSchema = csvFormSchema;

export const CreateLessonsByCsv = ({ trigger }: CreateLessonsByCsvProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);
  const [warningMessages, setWarningMessages] = useState<string[]>(EMPTY_WARNINGS);
  const { mutateAsync } = useMutation({
    mutationFn: createLessonsByCsv,
  });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { csvFile } = formData;

    const result = await parseCsvAndUpload({
      file: csvFile[0],
      rowSchema: createLessonCsvRow,
      onUpload: async (file) => {
        const uploadForm = new FormData();
        uploadForm.append(CreateLessonCsvFormKeys.csvFile, file);
        return mutateAsync(uploadForm);
      },
      onToast: toast,
      onSetErrors: setErrorMessages,
      onSetWarnings: setWarningMessages,
    });

    const { successRowsCount, hasErrors, hasWarnings } = result;

    if (successRowsCount > 0) {
      toast({ title: `${successRowsCount} new ${pluralize(successRowsCount, 'lesson', 'lessons')} created` });
      queryClient.invalidateQueries(getLessons());
    }

    if (!hasErrors && !hasWarnings) {
      closeModal();
    }

    return successRowsCount;
  };

  return (
    <FormModal
      modalTitle="Create lessons by CSV"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      onClose={() => {
        setErrorMessages(EMPTY_ERRORS);
        setWarningMessages(EMPTY_WARNINGS);
      }}
    >
      {({ form }) => {
        return (
          <>
            <div className="grid grid-cols-1 gap-2 py-4">
              <FormInputFile
                form={form}
                name="csvFile"
                label="Lessons CSV"
                inputProps={{
                  accept: '.csv',
                  className: 'w-full',
                }}
              />
              <span>
                See{' '}
                <a
                  href="https://docs.google.com/spreadsheets/d/1rzbm2t8r1Wsa89DuYZEippWqLki20n_XV6Hp0KelsPw/edit?usp=sharing"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-blue-600 hover:underline"
                >
                  template
                </a>{' '}
                for CSV template. Export the sheet as csv file and edit it. Do NOT change the header row
              </span>
            </div>
            {warningMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="warning">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Warnings while uploading CSV</AlertTitle>
                  <AlertDescription>
                    {warningMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
            {errorMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error while parsing CSV</AlertTitle>
                  <AlertDescription>
                    {errorMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
};
